const dateTimeFormat = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'short',
  timeStyle: 'short',
});

/** ISO (UTC) → local human date, e.g. "19/09/2026 22:30". */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return dateTimeFormat.format(date);
}

/** ISO → value for `<input type="datetime-local">` (local time, no seconds). */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}`;
}

/** `<input type="datetime-local">` value → ISO UTC string. */
export function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

export function truncate(value: string, max = 120): string {
  return value.length <= max ? value : `${value.slice(0, max)}… (${value.length} car.)`;
}

/** Deep copy with every `maquetteBillet` shortened — a full payload is ~63 000 chars per ticket. */
export function truncateMaquettes(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(truncateMaquettes);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, val]) => [
        key,
        key === 'maquetteBillet' && typeof val === 'string' ? truncate(val, 32) : truncateMaquettes(val),
      ]),
    );
  }
  return value;
}

export function formatOffset(seconds: number): string {
  const sign = seconds < 0 ? '-' : '+';
  const abs = Math.abs(seconds);
  const days = Math.floor(abs / 86400);
  const hours = Math.floor((abs % 86400) / 3600);
  const minutes = Math.floor((abs % 3600) / 60);
  const rest = abs % 60;
  const parts = [days && `${days} j`, hours && `${hours} h`, minutes && `${minutes} min`, rest && `${rest} s`].filter(
    Boolean,
  );
  return parts.length ? `${sign}${parts.join(' ')}` : 'aucun décalage';
}

const dayFormat = new Intl.DateTimeFormat('fr-FR', { day: '2-digit' });
const monthFormat = new Intl.DateTimeFormat('fr-FR', { month: 'short' });

/** Chip date de la carte événement : « 17 » + « SEPT. ». */
export function formatDayChip(iso: string | null | undefined): { day: string; month: string } {
  if (!iso) return { day: '—', month: '' };
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return { day: '—', month: '' };
  return { day: dayFormat.format(date), month: monthFormat.format(date).replace('.', '').toUpperCase() };
}

/** Sous-titre de la carte événement : « Lieu, Ville - JJ/MM/AA - HHh ». */
export function formatEventSubtitle(iso: string | null | undefined, lieu?: string | null, ville?: string | null): string {
  const place = [lieu, ville].filter(Boolean).join(', ');
  const date = new Date(iso ?? '');
  if (!iso || Number.isNaN(date.getTime())) return place;
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${String(date.getFullYear()).slice(-2)}`;
  const heure = `${date.getHours()}h${date.getMinutes() ? pad(date.getMinutes()) : ''}`;
  return [place, stamp, heure].filter(Boolean).join(' - ');
}
